<template>
  <b-card>
    <validation-observer
      ref="infoRules"
      v-slot="{ invalid }"
      tag="form"
      @submit.prevent
    >
      <b-row>
        <b-col md="6">
          <validation-provider
            #default="validationContext"
            name="Name"
            rules="required"
          >
            <b-form-group
              label="Name"
              label-for="sfda-lab-name"
            >
              <b-form-input
                id="sfda-lab-name"
                v-model="labForm.name"
                :state="getValidationState(validationContext)"
                trim
                placeholder="Lab name"
              />
              <b-form-invalid-feedback>
                {{ validationContext.errors[0] }}
              </b-form-invalid-feedback>
            </b-form-group>
          </validation-provider>
        </b-col>

        <b-col md="6">
          <validation-provider
            #default="validationContext"
            name="Valid Until"
            rules="required"
          >
            <b-form-group
              label="Valid until"
              label-for="sfda-lab-valid-until"
              description="Format: YYYY-MM-DD"
            >
              <b-form-input
                id="sfda-lab-valid-until"
                v-model="labForm.valid_until"
                type="date"
                :state="getValidationState(validationContext)"
                placeholder="YYYY-MM-DD"
              />
              <b-form-invalid-feedback>
                {{ validationContext.errors[0] }}
              </b-form-invalid-feedback>
            </b-form-group>
          </validation-provider>
        </b-col>

        <!-- Certificate number -->
        <b-col md="6">
          <b-form-group
            label="Certificate number"
            label-for="sfda-lab-certificate-number"
          >
            <b-form-input
              id="sfda-lab-certificate-number"
              v-model="labForm.certificate_number"
              placeholder="Certificate number"
            />
          </b-form-group>
        </b-col>

        <!-- Addresses (list of strings) -->
        <b-col md="6">
          <b-form-group
            label="Addresses"
            label-for="sfda-lab-addresses"
            description="Type an address, then press Enter to add. You can remove any item."
          >
            <div class="d-flex align-items-center">
              <b-form-input
                id="sfda-lab-addresses"
                v-model="addressInput"
                placeholder="Enter address and press Enter"
                @keyup.enter.prevent="addAddress"
              />
              <b-button
                variant="outline-primary"
                class="ml-1"
                :disabled="!addressInput || !addressInput.trim()"
                @click="addAddress"
              >
                Add
              </b-button>
            </div>

            <div
              v-if="addresses.length"
              class="mt-50"
            >
              <div class="address-chips">
                <span
                  v-for="(addr, idx) in addresses"
                  :key="`${addr}-${idx}`"
                  class="address-chip"
                >
                  <feather-icon
                    icon="MapPinIcon"
                    size="14"
                    class="mr-50"
                  />
                  <span class="address-chip__text">
                    {{ addr }}
                  </span>
                  <b-button
                    variant="link"
                    class="address-chip__remove p-0 ml-50"
                    @click="removeAddress(idx)"
                  >
                    <feather-icon
                      icon="XIcon"
                      size="14"
                    />
                  </b-button>
                </span>
              </div>
            </div>

            <small
              v-else
              class="text-muted d-block mt-25"
            >
              No addresses added yet.
            </small>
          </b-form-group>
        </b-col>

        <!-- Attachments (create) -->
        <b-col
          v-if="!$route.params.id"
          md="12"
        >
          <b-form-group label="Attachments">
            <b-form-file
              v-model="newAttachments"
              multiple
              accept=".pdf,.doc,.docx,.png,.jpg,.jpeg"
              placeholder="Choose files..."
            />
            <div
              v-if="newAttachmentsList.length"
              class="mt-50"
            >
              <div class="text-muted small mb-25">
                Selected files ({{ newAttachmentsList.length }})
              </div>
              <div class="d-flex flex-column">
                <div
                  v-for="(file, idx) in newAttachmentsList"
                  :key="`${file.name}-${file.size}-${idx}`"
                  class="d-flex align-items-center justify-content-between py-25"
                >
                  <span class="text-truncate small">{{ file.name }}</span>
                  <b-button
                    size="sm"
                    variant="outline-danger"
                    @click="removeNewAttachment(idx)"
                  >
                    <feather-icon
                      icon="XIcon"
                      size="14"
                      class="mr-25"
                    />
                    Remove
                  </b-button>
                </div>
              </div>
            </div>
            <small class="text-muted d-block mt-25">
              You can upload multiple files.
            </small>
          </b-form-group>
        </b-col>

        <!-- Existing attachments (edit) -->
        <b-col
          v-if="$route.params.id"
          md="12"
        >
          <b-form-group label="Current attachments">
            <div
              v-if="!existingAttachments.length"
              class="text-muted"
            >
              No attachments.
            </div>
            <div
              v-else
              class="d-flex flex-column"
            >
              <div
                v-for="att in existingAttachments"
                :key="att.id"
                class="d-flex align-items-center justify-content-between py-25"
              >
                <div class="d-flex align-items-center">
                  <a
                    v-if="att.url"
                    :href="att.url"
                    target="_blank"
                    class="mr-1"
                    title="Open attachment"
                  >
                    <feather-icon
                      icon="FileIcon"
                      size="18"
                      class="text-dark cursor-pointer"
                    />
                  </a>
                  <span class="text-muted small">
                    {{ att.name || att.file_name || `Attachment #${att.id}` }}
                  </span>
                </div>

                <b-button
                  size="sm"
                  variant="outline-danger"
                  :disabled="deleteLoadingId === att.id || loader || attachmentsLoader"
                  @click="deleteAttachment(att)"
                >
                  <b-spinner
                    v-if="deleteLoadingId === att.id"
                    small
                    class="mr-50"
                  />
                  Delete
                </b-button>
              </div>
            </div>
          </b-form-group>
        </b-col>

        <!-- Add new attachments (edit) -->
        <b-col
          v-if="$route.params.id"
          md="12"
        >
          <b-form-group label="Upload new attachments">
            <b-form-file
              v-model="newAttachments"
              multiple
              accept=".pdf,.doc,.docx,.xlsx,.xls,.xlsm,.png,.jpg,.jpeg"
              placeholder="Choose files..."
            />
            <div
              v-if="newAttachmentsList.length"
              class="mt-50"
            >
              <div class="text-muted small mb-25">
                Selected files ({{ newAttachmentsList.length }})
              </div>
              <div class="d-flex flex-column">
                <div
                  v-for="(file, idx) in newAttachmentsList"
                  :key="`${file.name}-${file.size}-${idx}`"
                  class="d-flex align-items-center justify-content-between py-25"
                >
                  <span class="text-truncate small">{{ file.name }}</span>
                  <b-button
                    size="sm"
                    variant="outline-danger"
                    :disabled="attachmentsLoader"
                    @click="removeNewAttachment(idx)"
                  >
                    <feather-icon
                      icon="XIcon"
                      size="14"
                      class="mr-25"
                    />
                    Remove
                  </b-button>
                </div>
              </div>
            </div>
            <div class="mt-50">
              <b-button
                variant="outline-primary"
                :disabled="attachmentsLoader || !newAttachments || !newAttachments.length"
                @click="uploadAttachments()"
              >
                <b-spinner
                  v-if="attachmentsLoader"
                  small
                  class="mr-50"
                />
                Upload attachments
              </b-button>
            </div>
          </b-form-group>
        </b-col>

        <!-- Server Errors -->
        <b-col
          v-if="Object.values(errors).length > 0"
          cols="12"
        >
          <p
            v-for="(error, key, index) in errors"
            :key="index"
            class="text-danger small"
          >
            {{ error.toString() }}
          </p>
        </b-col>
      </b-row>

      <b-row>
        <b-col
          cols="12"
          class="mt-50"
        >
          <b-button
            v-if="!loader && $route.params.id"
            variant="primary"
            class="mr-1"
            :disabled="invalid || Object.values(errors).length > 0"
            @click="saveLab()"
          >
            Save Changes
          </b-button>
          <b-button
            v-if="!loader && !$route.params.id"
            variant="primary"
            class="mr-1"
            :disabled="invalid || Object.values(errors).length > 0"
            @click="saveLab()"
          >
            Add
          </b-button>
          <b-button
            v-if="loader"
            variant="primary"
            disabled
            class="mr-1"
          >
            <b-spinner
              small
              type="grow"
              class="mr-50"
            />
            Loading...
          </b-button>
        </b-col>
      </b-row>
    </validation-observer>
  </b-card>
</template>

<script>
import { ref } from '@vue/composition-api'
import formValidation from '@core/comp-functions/forms/form-validation'
import axios from 'axios'
import { required } from '@validations'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import decryptData from '@/store/auth/decryptData'

export default {
  name: 'AddEditSFDALabs',
  data() {
    return {
      required,
      loader: false,
      attachmentsLoader: false,
      deleteLoadingId: null,
      newAttachments: [],
      errors: {},
      addresses: [],
      addressInput: '',
    }
  },
  setup() {
    const { getValidationState } = formValidation()
    const labForm = ref({
      name: '',
      valid_until: '',
      certificate_number: '',
    })

    return {
      getValidationState,
      labForm,
    }
  },
  computed: {
    existingAttachments() {
      return Array.isArray(this.labForm.attachments) ? this.labForm.attachments : []
    },
    newAttachmentsList() {
      if (Array.isArray(this.newAttachments)) return this.newAttachments
      if (this.newAttachments) return [this.newAttachments]
      return []
    },
  },
  mounted() {
    this.fetchLab()
  },
  methods: {
    async fetchLab() {
      if (!this.$route.params.id) return
      this.loader = true
      try {
        const res = await axios.get(`/sfda-labs/${this.$route.params.id}`)
        const payload = decryptData(res.data.payload)
        const data = payload?.data || payload
        const key = Object.keys(data || {}).find(k => data[k] && typeof data[k] === 'object' && !Array.isArray(data[k]))
        const record = key ? data[key] : data

        this.labForm = {
          ...this.labForm,
          ...record,
          name: record?.name || '',
          valid_until: record?.valid_until || '',
          certificate_number: record?.certificate_number || '',
          attachments: Array.isArray(record?.attachments) ? record.attachments : [],
        }
        this.addresses = Array.isArray(record?.addresses) ? record.addresses : []
      } catch (e) {
        // silent
      } finally {
        this.loader = false
      }
    },
    toastErrorFromResponse(error, fallback) {
      try {
        const payload = decryptData(error.response.data.payload).message
        const firstArrayKey = Object.keys(payload).find(key => Array.isArray(payload[key]))
        const errorMessage = firstArrayKey ? payload[firstArrayKey][0] : fallback
        this.$toast({
          component: ToastificationContent,
          props: {
            title: errorMessage,
            icon: 'BellIcon',
            variant: 'error',
          },
        })
      } catch (e) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: fallback,
            icon: 'BellIcon',
            variant: 'error',
          },
        })
      }
    },
    buildAttachmentsFormData(files) {
      const formData = new FormData()
      const list = Array.isArray(files) ? files : []
      list.forEach((file, idx) => {
        formData.append(`attachments[${idx}]`, file)
      })
      return formData
    },
    removeNewAttachment(idx) {
      const list = this.newAttachmentsList.slice()
      list.splice(idx, 1)
      this.newAttachments = list
    },
    addAddress() {
      const val = (this.addressInput || '').trim()
      if (!val) return

      // Avoid duplicates while the user is typing/adding multiple times.
      if (!this.addresses.includes(val)) {
        this.addresses.push(val)
      }

      this.addressInput = ''
    },
    removeAddress(idx) {
      const list = this.addresses.slice()
      list.splice(idx, 1)
      this.addresses = list
    },
    async saveLab() {
      const addressesList = Array.isArray(this.addresses)
        ? this.addresses.map(a => String(a).trim()).filter(Boolean)
        : []

      if (this.$route.params.id) {
        this.loader = true
        const formData = new FormData()
        formData.append('_method', 'put')
        formData.append('name', this.labForm.name || '')
        formData.append('valid_until', this.labForm.valid_until || '')
        formData.append('certificate_number', this.labForm.certificate_number || '')
        addressesList.forEach((addr, idx) => {
          formData.append(`addresses[${idx}]`, addr)
        })

        try {
          const res = await axios.post(`/sfda-labs/${this.$route.params.id}`, formData)
          if (res.status === 200) {
            // If user selected new files while editing, upload them too
            if (this.newAttachmentsList.length) {
              this.attachmentsLoader = true
              try {
                const uploadData = this.buildAttachmentsFormData(this.newAttachmentsList)
                const uploadRes = await axios.post(`/sfda-labs/${this.$route.params.id}/attachments`, uploadData)
                if (uploadRes.status === 200 || uploadRes.status === 201) {
                  this.$toasted.show('Attachments uploaded', {
                    position: 'top-center',
                    duration: 2500,
                  })
                  this.newAttachments = []
                }
              } catch (error) {
                if (error?.response?.status) {
                  this.toastErrorFromResponse(error, 'Failed to upload attachments')
                }
              } finally {
                this.attachmentsLoader = false
              }
            }

            this.$router.push({ name: 'sfda-labs' })
            this.$toasted.show('Updated Successfully', {
              position: 'top-center',
              duration: 3000,
            })
          }
        } catch (error) {
          if (error?.response?.status) {
            this.toastErrorFromResponse(error, 'Failed to update SFDA lab')
          }
        } finally {
          this.loader = false
        }
      } else {
        this.loader = true
        const formData = new FormData()
        formData.append('name', this.labForm.name || '')
        formData.append('valid_until', this.labForm.valid_until || '')
        formData.append('certificate_number', this.labForm.certificate_number || '')
        addressesList.forEach((addr, idx) => {
          formData.append(`addresses[${idx}]`, addr)
        })
        this.newAttachmentsList.forEach((file, idx) => {
          formData.append(`attachments[${idx}]`, file)
        })

        try {
          const res = await axios.post('sfda-labs', formData)
          if (res.status === 200 || res.status === 201) {
            this.$router.push({ name: 'sfda-labs' })
            this.$toasted.show('Created Successfully', {
              position: 'top-center',
              duration: 3000,
            })
          }
        } catch (error) {
          if (error?.response?.status) {
            this.toastErrorFromResponse(error, 'Failed to create SFDA lab')
          }
        } finally {
          this.loader = false
        }
      }
    },
    async uploadAttachments() {
      if (!this.$route.params.id) return
      if (!this.newAttachmentsList.length) return

      this.attachmentsLoader = true
      try {
        const formData = this.buildAttachmentsFormData(this.newAttachmentsList)
        const res = await axios.post(`/sfda-labs/${this.$route.params.id}/attachments`, formData)
        if (res.status === 200 || res.status === 201) {
          this.$toasted.show('Attachments uploaded', {
            position: 'top-center',
            duration: 2500,
          })
          this.newAttachments = []
          await this.fetchLab()
        }
      } catch (error) {
        if (error?.response?.status) {
          this.toastErrorFromResponse(error, 'Failed to upload attachments')
        }
      } finally {
        this.attachmentsLoader = false
      }
    },
    async deleteAttachment(att) {
      if (!this.$route.params.id || !att?.id) return

      const result = await this.$swal({
        title: 'Delete attachment?',
        text: "You won't be able to revert this!",
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#d33',
        cancelButtonColor: '#3085d6',
        confirmButtonText: 'Delete',
      })

      if (!result.isConfirmed) return

      this.deleteLoadingId = att.id
      try {
        await axios.delete(`/sfda-labs/${this.$route.params.id}/attachments/${att.id}`)
        this.$toasted.show('Deleted Successfully', {
          position: 'top-center',
          duration: 2500,
        })
        await this.fetchLab()
      } catch (error) {
        if (error?.response?.status) {
          this.toastErrorFromResponse(error, 'Failed to delete attachment')
        }
      } finally {
        this.deleteLoadingId = null
      }
    },
  },
}
</script>

<style scoped>
.address-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.address-chip {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  border-radius: 999px;
  border: 1px solid rgba(6, 182, 212, 0.28);
  background: rgba(6, 182, 212, 0.08);
  padding: 0.35rem 0.6rem;
  max-width: 100%;
}

.address-chip__text {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 22rem;
}

.address-chip__remove {
  color: #ea5455;
}

.address-chip__remove:hover {
  color: #ea5455;
}
</style>
